import AsyncSelect from "react-select/async";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { Col, Modal, ModalBody, ModalHeader, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import PaginationComponent from "../../../components/Pagination";
import UserAvatar from "../../../components/UserAvatar";
import Currency, {
  ConvertCurrency,
  ConvertCurrencyToText,
} from "../../../helpers/Currency/Currency";
import Badge from "../../../components/Badge";
import InfoItem from "../../../helpers/InfoItem/InfoItem";
import ErrorMessage from "../../../components/ErrorMessage";
import PaymentGateway from "../../../components/PaymentGateway";
import DataExporter from "../../../components/DataExporter";
import DocumentsSection, {
  DocumentsDetails,
} from "../../../components/DocumentsSection";
import { transactionUserContext } from "./../../../context/UserContext/TransactionUserContext/TransactionUserContext";
import {
  convertTransactionTypeToColor,
  convertTransactionTypeToIcon,
  convertTransactionTypeToText,
  createTransactionTypeItems,
  exporterData,
} from "../../../utils/enum";
import {
  convertDate,
  numberSeparate,
  removeNumberSeparate,
} from "../../../utils";
import {
  chargeWalletValidation,
  createTransactionValidation,
  editTransactionValidation,
} from "../../../utils/validation/validation";

const TransactionsUser = () => {
  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);
  const loader = useSelector((state) => state.loader);

  const { user: userId } = useParams();

  const {
    transactions,
    transaction,
    setTransaction,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    type,
    setType,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    setIsShowChargeModal,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleGetTransactions,
    handleCreateTransaction,
    handleEditTransaction,
  } = useContext(transactionUserContext);

  useEffect(() => {
    handleGetTransactions();
  }, [start, end, pageNumber, itemsCount, sort, userId, type]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      isEmpty(transaction)
        ? createTransactionValidation
        : editTransactionValidation,
    ),
  });

  return (
    <>
      <SiteTitle title="تراکنشات" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setTransactionType={setType}
        transactionType={type}
      />

      <div className="row gy-4">
        <div className="col-12 col-lg-3">
          <div className="card h-100">
            <div className="card-body p-0">
              <div className="px-24 py-20">
                <span className="mb-8">موجودی کیف پول</span>
                <h5 className="text-3xl">
                  <ConvertCurrency price={user.wallet} />{" "}
                  <Currency className="text-sm" />
                </h5>
                <div className="mt-24 pb-24 mb-24 border-bottom d-flex align-items-center gap-16 justify-content-between flex-wrap">
                  <div
                    className="text-center d-flex align-items-center flex-column cursor-pointer"
                    onClick={() => setIsShowChargeModal(true)}
                  >
                    <span className="w-60-px h-60-px bg-light-50 text-primary text-2xl d-inline-flex justify-content-center align-items-center rounded-circle">
                      <i className="ri-add-line" />
                    </span>
                    <span className="text-primary fw-medium mt-6">افزایش</span>
                  </div>
                  <div className="text-center d-flex align-items-center flex-column">
                    <span className="w-60-px h-60-px bg-light-50 text-primary-200 text-2xl d-inline-flex justify-content-center align-items-center rounded-circle ">
                      <i className="ri-upload-2-line" />
                    </span>
                    <span className="text-primary-200 fw-medium mt-6">
                      برداشت
                    </span>
                  </div>
                  <div className="text-center d-flex align-items-center flex-column">
                    <span className="w-60-px h-60-px bg-light-50 text-primary-200 text-2xl d-inline-flex justify-content-center align-items-center rounded-circle ">
                      <i className="ri-arrow-left-right-line" />
                    </span>
                    <span className="text-primary-200 fw-medium mt-6">
                      انتقال
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-9">
          <div className="card h-100 p-0 radius-12">
            <SearchTable
              endItem={
                <div className="d-flex justify-content-between align-items-center gap-2">
                  {!isEmpty(transactions) && (
                    <DataExporter
                      headers={exporterData.transactions.headers}
                      keys={exporterData.transactions.keys}
                      data={transactions}
                      fileName={exporterData.transactions.fileName}
                    />
                  )}
                  {auth.isAdmin && (
                    <button
                      className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                      onClick={() => {
                        handleShowModal();
                        reset();
                      }}
                    >
                      <Icon icon="ic:baseline-plus" className="text-xl" />
                      افزودن تراکنش
                    </button>
                  )}
                </div>
              }
              filterPlaceholder="کدپیگیری، توضیحات و..."
              filter={filter}
              setFilter={setFilter}
              onSearch={handleGetTransactions}
            />
            <div className="card-body p-24">
              {isEmpty(transactions) ? (
                <EmptyData />
              ) : (
                <>
                  <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          {auth.isAdmin && <th scope="col">کاربر</th>}
                          <th scope="col">
                            مبلغ <Currency />
                          </th>
                          <th scope="col">
                            موجودی بعد از عملیات <Currency />
                          </th>
                          <th scope="col">نوع</th>
                          <th scope="col">کد پیگیری</th>
                          <th scope="col">تاریخ</th>
                          <th scope="col">تعداد اسناد</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((item, index) => {
                          const color = convertTransactionTypeToColor(
                            item.type,
                          );
                          return (
                            <tr>
                              <td>{index + 1}</td>
                              {auth.isAdmin && (
                                <td>
                                  <div className="d-flex align-items-center">
                                    <UserAvatar
                                      className="ms-2"
                                      {...(item?.user?.avatar
                                        ? { image: item?.user?.avatar }
                                        : {
                                            icon: "solar:user-broken",
                                          })}
                                    />
                                    <div className="flex-grow-1">
                                      <span className="text-md mb-0 fw-normal text-secondary-light">
                                        {item?.user?.firstName}{" "}
                                        {item?.user?.lastName}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td>
                                <ConvertCurrency price={item.amount} />
                              </td>
                              <td>
                                <Badge
                                  className="badge-dim text-sm"
                                  color={color}
                                >
                                  <ConvertCurrency price={item.walletBalance} />
                                  <i
                                    className={convertTransactionTypeToIcon(
                                      item.type,
                                    )}
                                  />
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  className="badge-dim text-sm"
                                  color={color}
                                >
                                  {convertTransactionTypeToText(item.type)}
                                </Badge>
                              </td>
                              <td>{item.trackingCode}</td>
                              <td>
                                {convertDate(
                                  item.createdAt,
                                  "jYYYY/jMM/jDD - HH:mm:ss",
                                )}
                              </td>
                              <td>{numberSeparate(item.documents?.length)}</td>
                              <td>
                                <div className="dropdown">
                                  <button
                                    className="hover-bg-transparent"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <Icon
                                      icon="ph:dots-three-outline-fill"
                                      className="menu-icon"
                                    />
                                  </button>
                                  <ul className="dropdown-menu list">
                                    {auth.isAdmin && (
                                      <li
                                        onClick={() => {
                                          reset();
                                          handleShowModal(item, setValue);
                                        }}
                                        className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                      >
                                        ویرایش
                                      </li>
                                    )}
                                    <li
                                      onClick={() => setTransaction(item)}
                                      className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                    >
                                      جزئیات
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <PaginationComponent
                    pageCount={pageCount}
                    currentPage={pageNumber}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(transaction) ? "افزودن تراکنش جدید" : "ویرایش تراکنش"}
          </span>
        </ModalHeader>
        <ModalBody>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(transaction)
                ? handleCreateTransaction
                : handleEditTransaction,
            )}
          >
            <div className="row">
              {isEmpty(transaction) && (
                <>
                  <div className="col-12">
                    <div className="mb-20">
                      <div>
                        <label
                          htmlFor="category"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          کاربر
                        </label>
                        <Controller
                          control={control}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <AsyncSelect
                              defaultOptions
                              openMenuOnFocus
                              cacheOptions
                              className="react-select-container"
                              classNamePrefix="react-select"
                              placeholder="لطفا کاربر را انتخاب نمایید"
                              isLoading={loader}
                              onBlur={onBlur}
                              noOptionsMessage={() => (
                                <p>کاربری برای نمایش یافت نشد</p>
                              )}
                              loadingMessage={() => (
                                <p>لطفا منتظر بمانید ...</p>
                              )}
                              loadOptions={(val, callback) => {
                                handleGetUsers(val, callback);
                              }}
                              onChange={onChange}
                              value={value}
                              isClearable
                            />
                          )}
                          name="user"
                        />
                      </div>

                      <ErrorMessage
                        message={
                          errors.user?.message || errors.user?.value?.message
                        }
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="mb-20">
                      <div>
                        <label
                          htmlFor="amount"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          مبلغ <Currency />
                        </label>
                        <Controller
                          control={control}
                          render={({ field: { value, onChange, onBlur } }) => (
                            <input
                              min={0}
                              type="text"
                              placeholder="مبلغ را وارد نمایید"
                              className="form-control radius-8"
                              onChange={(e) => {
                                const value = removeNumberSeparate(
                                  e.target.value,
                                );
                                onChange(value);
                              }}
                              onBlur={onBlur}
                              value={numberSeparate(value)}
                              inputMode="decimal"
                            />
                          )}
                          name="amount"
                        />
                        <ConvertCurrencyToText price={watch("amount")} />
                      </div>

                      <ErrorMessage message={errors.amount?.message} />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="mb-20">
                      <div>
                        <label
                          htmlFor="type"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          نوع تراکنش
                        </label>
                        <select
                          className="form-control radius-8"
                          {...register("type")}
                        >
                          <option value="" selected hidden>
                            نوع تراکنش را انتخاب نمایید
                          </option>
                          {createTransactionTypeItems.map((item) => (
                            <option
                              key={item}
                              value={item}
                              selected={watch("type") === item}
                            >
                              {convertTransactionTypeToText(item)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <ErrorMessage message={errors.type?.message} />
                    </div>
                  </div>
                </>
              )}
              <div className="col-sm-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="description"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      توضیحات
                    </label>
                    <textarea
                      name="description"
                      className="form-control radius-8"
                      id="description"
                      rows={5}
                      placeholder="توضیحات را وارد نمایید"
                      {...register("description")}
                    />
                  </div>
                  <ErrorMessage message={errors.description?.message} />
                </div>
              </div>
              <DocumentsSection
                watch={watch}
                setValue={setValue}
                register={register}
                errors={errors}
              />
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(transaction) ? "افزودن" : "ویرایش"} فاکتور
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        show={!isEmpty(transaction) && !transaction.isEdit}
        className="modal-dialog-centered"
        size="lg"
        onHide={() => setTransaction({})}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">جزئیات تراکنش</span>
        </ModalHeader>
        <ModalBody>
          {auth.isAdmin && (
            <>
              <span className="text-primary border-bottom fw-bold">
                اطلاعات کاربر
              </span>
              <Row>
                <Col sm="6">
                  <InfoItem title="نام">
                    {transaction?.user?.firstName}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="نام خانوادگی">
                    {transaction?.user?.lastName}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="شماره تماس">
                    {transaction?.user?.phoneNumber}
                  </InfoItem>
                </Col>
              </Row>
            </>
          )}

          <span className="text-primary border-bottom fw-bold">
            اطلاعات تراکنش
          </span>
          <Row className="g-2">
            <Col sm="6">
              <InfoItem title="مبلغ">
                <ConvertCurrency price={transaction?.amount} /> <Currency />
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="موجودی بعد از عملیات">
                <ConvertCurrency price={transaction?.walletBalance} />{" "}
                <Currency />
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="کد پیگیری">{transaction?.trackingCode}</InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="تاریخ">
                {convertDate(
                  transaction?.createdAt,
                  "jYYYY/jMM/jDD - HH:mm:ss",
                )}
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="نوع">
                <Badge
                  className="badge-dim"
                  color={convertTransactionTypeToColor(transaction?.type)}
                >
                  {convertTransactionTypeToText(transaction?.type)}
                </Badge>
              </InfoItem>
            </Col>
            {!!transaction?.description && (
              <Col xs="12">
                <div className="alert alert-info bg-info-50 text-info-500 border-info-100 py-1 mt-1 text-sm radius-10">
                  <div className="d-flex align-items-center gap-2 text-pre-wrap">
                    <Icon icon="solar:danger-triangle-linear" />
                    {transaction?.description}
                  </div>
                </div>
              </Col>
            )}
          </Row>

          <DocumentsDetails documents={transaction?.documents} />
        </ModalBody>
      </Modal>

      <ChargeWallet />
    </>
  );
};

export default TransactionsUser;

const ChargeWallet = () => {
  const { isShowChargeModal, setIsShowChargeModal, handleChargeWallet } =
    useContext(transactionUserContext);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(chargeWalletValidation),
  });

  useEffect(() => {
    reset();
  }, [isShowChargeModal]);

  return (
    <Modal
      show={isShowChargeModal}
      className="modal-dialog-centered"
      size="lg"
      onHide={() => setIsShowChargeModal(false)}
    >
      <ModalHeader closeButton>
        <span className="fs-5 fw-bold">شارژ کیف پول</span>
      </ModalHeader>
      <ModalBody>
        <form autoComplete="off" onSubmit={handleSubmit(handleChargeWallet)}>
          <div className="row">
            <div className="col-12">
              <div className="mb-20">
                <div>
                  <label
                    htmlFor="amount"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    مبلغ <Currency />
                  </label>
                  <Controller
                    control={control}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <input
                        min={0}
                        type="text"
                        placeholder="مبلغ را وارد نمایید"
                        className="form-control radius-8"
                        onChange={(e) => {
                          const value = removeNumberSeparate(e.target.value);
                          onChange(value);
                        }}
                        onBlur={onBlur}
                        value={numberSeparate(value)}
                        inputMode="decimal"
                      />
                    )}
                    name="amount"
                  />
                  <ConvertCurrencyToText price={watch("amount")} />
                </div>

                <ErrorMessage message={errors.amount?.message} />
              </div>
            </div>

            <div className="col-sm-12">
              <div className="mb-20">
                <label
                  htmlFor="description"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  توضیحات
                </label>
                <textarea
                  name="description"
                  className="form-control radius-8"
                  id="description"
                  rows={5}
                  placeholder="توضیحات را وارد نمایید"
                  {...register("description")}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="mb-20">
                <Controller
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <PaymentGateway
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  )}
                  name="gateway"
                />

                <ErrorMessage message={errors.gateway?.message} />
              </div>
            </div>
          </div>

          <div className="text-end mt-3">
            <button
              type="submit"
              className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
            >
              پرداخت
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};
